import React, { useEffect, useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { AAL_QUERY_KEY } from '@/lib/auth/aal';
import { completeStepUp, isTotpCode, StepUpUnavailableError } from '@/lib/auth/step-up';
import { handleError } from '@/lib/errors';

/**
 * The MFA step-up, in place. Opened when the server refuses an action with
 * 403 `aal2_required`; on success the caller retries that same action, so the
 * person never dead-ends on "sign out and back in".
 */
export function StepUpModal({
  visible,
  reason,
  onClose,
  onVerified,
}: {
  visible: boolean;
  /** What the code unlocks, e.g. "delete your account". */
  reason: string;
  onClose: () => void;
  onVerified: () => void;
}) {
  const colors = useThemeColors();
  const queryClient = useQueryClient();
  const [code, setCode] = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setCode('');
      setMessage(null);
    }
  }, [visible]);

  const verify = useMutation<void, Error, string>({
    mutationFn: completeStepUp,
    onSuccess: async () => {
      // Every guard reads the assurance level through this query.
      await queryClient.invalidateQueries({ queryKey: AAL_QUERY_KEY });
      onVerified();
    },
    onError: (err) => {
      const { message: shown, isCancelled } = handleError(err, { context: 'StepUpModal.verify' });
      if (isCancelled) return;
      setCode('');
      setMessage(
        err instanceof StepUpUnavailableError
          ? 'No authenticator is set up on this account. Contact privacy@inverio.ai.'
          : shown || 'That code did not work. Try the current one from your authenticator app.',
      );
    },
  });

  const canVerify = isTotpCode(code) && !verify.isPending;
  const submit = () => {
    if (canVerify) verify.mutate(code);
  };

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title="Confirm it's you"
      maxWidth={420}
      footer={
        <AppModal.Actions>
          <ModalAction label="Not now" onPress={onClose} />
          <ModalAction
            label={verify.isPending ? 'Verifying…' : 'Verify'}
            variant="primary"
            onPress={submit}
            disabled={!canVerify}
          />
        </AppModal.Actions>
      }
    >
      <View className="items-center" style={{ gap: 12 }}>
        <View
          className="items-center justify-center"
          style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: colors.primary[50] }}
        >
          <ShieldCheck size={26} color={colors.primary[600]} />
        </View>
        <Text numberOfLines={3} style={{ color: colors.text.secondary, fontSize: 15, textAlign: 'center', lineHeight: 21 }}>
          Enter the 6-digit code from your authenticator app to {reason}.
        </Text>
        <TextInput
          value={code}
          onChangeText={(t) => {
            setMessage(null);
            setCode(t.replace(/\D/g, '').slice(0, 6));
          }}
          onSubmitEditing={submit}
          keyboardType="number-pad"
          textContentType="oneTimeCode"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          accessibilityLabel="Authenticator code"
          placeholder="000000"
          placeholderTextColor={colors.text.tertiary}
          style={{
            alignSelf: 'stretch',
            height: 56,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: message ? colors.error : colors.border.medium,
            backgroundColor: colors.surface.secondary,
            color: colors.text.primary,
            fontSize: 26,
            letterSpacing: 10,
            textAlign: 'center',
            fontVariant: ['tabular-nums'],
          }}
        />
        {!!message && (
          <Text numberOfLines={3} style={{ color: colors.error, fontSize: 13, textAlign: 'center' }}>
            {message}
          </Text>
        )}
      </View>
    </AppModal>
  );
}
